import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  SEARCH_FILTER_ACCOLADE_LABEL,
  SEARCH_FILTER_ATMOSPHERE_LABEL,
  SEARCH_FILTER_CUISINE_LABEL,
  SEARCH_FILTER_NEIGHBORHOOD_LABEL,
  SEARCH_FILTER_PRICE_RANGE_LABEL,
} from '../../../assets/copy';
import {
  actionResetCurrentFilters,
  actionSetCurrentFilters,
} from '../../../store/Filter/FilterAction';
import { FILTER_TYPES } from '../../../utils/constants/FilterTypes';
import FilterModalContainer from './FilterModalContainer';
import FilterSection from './FilterSection';
import { useFilterLists } from './helpers/useFilterLists';
import { ButtonList, CheckboxList } from './SectionList';
import {
  trackClickWithDescription,
  trackSiteSearch,
} from '../../../utils/useOneTag';

const Filter = () => {
  const dispatch = useDispatch();
  const { currentFilters } = useSelector((state) => state.filters);
  const ref = useRef(null);
  const {
    priceRangeList,
    neighborhoodsList,
    atmospheresList,
    cuisinesList,
    accoladesList,
  } = useFilterLists();

  const [selectFilters, setSelectFilters] = useState(currentFilters);

  const handleApplyFilters = useCallback(() => {
    trackSiteSearch(
      '',
      '',
      JSON.stringify(
        Object.fromEntries(
          Object.entries(selectFilters).map(([key, value]) => [
            key,
            value.map((item) => item.name),
          ])
        )
      )
    );

    dispatch(actionSetCurrentFilters(selectFilters));
  }, [dispatch, selectFilters]);

  const handleFilterSelect = (item, type) => {
    const filterWithoutItem = selectFilters[type].filter(
      (i) => i.id !== item.id
    );
    if (filterWithoutItem.length === selectFilters[type].length) {
      trackClickWithDescription(
        'search-revamp-filter-selection',
        'Button',
        'select ' + item.name
      );
      setSelectFilters({
        ...selectFilters,
        [type]: [...selectFilters[type], { ...item, timestamp: new Date() }],
      });
    } else {
      trackClickWithDescription(
        'search-revamp-filter-selection',
        'Button',
        'unselect ' + item.name
      );
      setSelectFilters({
        ...selectFilters,
        [type]: filterWithoutItem,
      });
    }
  };

  const isFilterInAccordionSelected = (selectFiltersCategory) =>
    selectFiltersCategory?.length > 0;

  const handleResetFilters = useCallback(() => {
    dispatch(actionResetCurrentFilters());
  }, [dispatch]);

  // When currentFilters updates, set selectFilters to be currentFilters
  useEffect(() => {
    setSelectFilters(currentFilters);
  }, [currentFilters, setSelectFilters]);

  return (
    <FilterModalContainer
      onApply={handleApplyFilters}
      onReset={handleResetFilters}
      selectedFilters={selectFilters}
      onClose={() => setSelectFilters(currentFilters)}>
      <FilterSection
        title={SEARCH_FILTER_PRICE_RANGE_LABEL}
        list={priceRangeList}
        id={FILTER_TYPES.PRICE_RANGE}
        defaultExpanded={isFilterInAccordionSelected(selectFilters?.priceRange)}
        ref={ref}>
        <ButtonList
          list={priceRangeList}
          activeList={selectFilters.priceRange}
          onClick={handleFilterSelect}
          id={FILTER_TYPES.PRICE_RANGE}
        />
      </FilterSection>
      <FilterSection
        title={SEARCH_FILTER_ACCOLADE_LABEL}
        list={accoladesList}
        id={FILTER_TYPES.ACCOLADES}
        defaultExpanded={isFilterInAccordionSelected(selectFilters?.accolades)}>
        <ButtonList
          list={accoladesList}
          activeList={selectFilters.accolades}
          onClick={handleFilterSelect}
          id={FILTER_TYPES.ACCOLADES}
        />
      </FilterSection>
      <FilterSection
        title={SEARCH_FILTER_CUISINE_LABEL}
        list={cuisinesList}
        id={FILTER_TYPES.CUISINES}
        defaultExpanded={isFilterInAccordionSelected(selectFilters?.cuisines)}>
        <CheckboxList
          list={cuisinesList}
          activeList={selectFilters.cuisines}
          onClick={handleFilterSelect}
          id={FILTER_TYPES.CUISINES}
        />
      </FilterSection>
      <FilterSection
        title={SEARCH_FILTER_ATMOSPHERE_LABEL}
        list={atmospheresList}
        id={FILTER_TYPES.ATMOSPHERES}
        defaultExpanded={isFilterInAccordionSelected(selectFilters?.atmospheres)}>
        <CheckboxList
          list={atmospheresList}
          activeList={selectFilters.atmospheres}
          onClick={handleFilterSelect}
          id={FILTER_TYPES.ATMOSPHERES}
        />
      </FilterSection>
      <FilterSection
        title={SEARCH_FILTER_NEIGHBORHOOD_LABEL}
        list={neighborhoodsList}
        id={FILTER_TYPES.NEIGHBORHOODS}
        defaultExpanded={isFilterInAccordionSelected(
          selectFilters?.neighborhoods
        )}>
        <CheckboxList
          list={neighborhoodsList}
          activeList={selectFilters.neighborhoods}
          onClick={handleFilterSelect}
          id={FILTER_TYPES.NEIGHBORHOODS}
        />
      </FilterSection>
    </FilterModalContainer>
  );
};

export default Filter;
